import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  List,
  ListItemButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import PageMeta from 'components/seo/PageMeta';
import ErrorBoundary from 'components/feedback/ErrorBoundary';
import { buildInfo } from 'config/buildInfo';
import { ROUTES } from 'config/routes';
import { ADMIN_NAV_GROUPS, findActiveAdminLink } from 'config/adminNavigation';
import { useStoreAdminContext } from 'context/StoreAdminContext';
import { useTenant } from 'hooks/useTenant';
import { useStorefrontContext } from 'context/StorefrontContext';
import dashboardAdminService, { OPERATION_COUNTS_REFRESH } from 'services/admin/dashboardAdminService';
import { StoreOperationCounts } from 'types/admin';

const SELECTED_BG = 'rgba(25, 118, 210, 0.1)';
const HOVER_BG = 'rgba(25, 118, 210, 0.04)';
const EMPTY_COUNTS: StoreOperationCounts = { abandonedCarts: 0, pendingAccessRequests: 0 };

const countLabel = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const NotificationCount: React.FC<{ count: number; label: string; tone?: 'error' | 'warning' }> = ({
  count,
  label,
  tone = 'error',
}) => {
  if (count <= 0) return null;
  const text = count > 99 ? '99+' : String(count);
  return (
    <Tooltip title={label}>
      <Box
        component="span"
        aria-label={label}
        sx={{
          minWidth: 18,
          height: 18,
          px: 0.5,
          borderRadius: 9,
          bgcolor: `${tone}.main`,
          color: `${tone}.contrastText`,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: '18px',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const [counts, setCounts] = useState<StoreOperationCounts>(EMPTY_COUNTS);

  useEffect(() => {
    let active = true;
    const load = () => {
      dashboardAdminService
        .getOperationCounts()
        .then((data) => {
          if (active) setCounts(data);
        })
        .catch(() => {
          /* el menú sigue usable si el conteo no responde */
        });
    };
    load();
    const timer = window.setInterval(load, 60_000);
    window.addEventListener(OPERATION_COUNTS_REFRESH, load);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(OPERATION_COUNTS_REFRESH, load);
    };
  }, [location.pathname]);

  const cartLabel = countLabel(counts.abandonedCarts, 'carrito abandonado', 'carritos abandonados');
  const requestLabel = countLabel(counts.pendingAccessRequests, 'solicitud de acceso', 'solicitudes de acceso');
  const activeLink = findActiveAdminLink(location.pathname, location.search);
  const activeGroupId = ADMIN_NAV_GROUPS.find((group) =>
    group.links.some((link) => link.to === activeLink)
  )?.id;
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(activeGroupId ? [activeGroupId] : [])
  );

  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((current) => (current.has(activeGroupId) ? current : new Set(current).add(activeGroupId)));
  }, [activeGroupId]);

  const toggleGroup = (id: string) =>
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const summarySelected = location.pathname === ROUTES.ADMIN;

  return (
    <Stack component="nav" aria-label="administración tienda" spacing={1} sx={{ width: { md: 240 }, flexShrink: 0, alignSelf: { md: 'flex-start' } }}>
      <ListItemButton
        component={RouterLink}
        to={ROUTES.ADMIN}
        selected={summarySelected}
        sx={{
          flexGrow: 0,
          minHeight: 52,
          px: 1.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: summarySelected ? 'primary.main' : 'divider',
          bgcolor: 'background.paper',
          color: summarySelected ? 'primary.main' : 'text.primary',
          '&.Mui-selected, &.Mui-selected:hover': { bgcolor: SELECTED_BG },
          '&:hover': { borderColor: 'primary.main', bgcolor: HOVER_BG },
        }}
      >
        <DashboardOutlinedIcon fontSize="small" sx={{ mr: 1.25 }} />
        <Typography variant="body2" fontWeight={summarySelected ? 700 : 600}>
          Resumen
        </Typography>
      </ListItemButton>

      {ADMIN_NAV_GROUPS.map((group) => {
        const Icon = group.icon;
        if (group.to) {
          const selected = location.pathname === group.to.split('?')[0];
          return (
            <ListItemButton
              key={group.id}
              component={RouterLink}
              to={group.to}
              selected={selected}
              sx={{
                flexGrow: 0,
                minHeight: 52,
                px: 1.75,
                borderRadius: 2,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
                color: selected ? 'primary.main' : 'text.primary',
                '&.Mui-selected, &.Mui-selected:hover': { bgcolor: SELECTED_BG },
                '&:hover': { borderColor: 'primary.main', bgcolor: HOVER_BG },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.25 }} />
              <Typography variant="body2" fontWeight={selected ? 700 : 600}>
                {group.title}
              </Typography>
            </ListItemButton>
          );
        }
        const open = openGroups.has(group.id);
        const containsActive = group.id === activeGroupId;
        return (
          <Box
            key={group.id}
            sx={{
              border: '1px solid',
              borderColor: containsActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <ListItemButton
              onClick={() => toggleGroup(group.id)}
              aria-expanded={open}
              sx={{
                minHeight: 52,
                px: 1.75,
                color: containsActive ? 'primary.main' : 'text.primary',
                '&:hover': { bgcolor: HOVER_BG },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.25 }} />
              <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
                {group.title}
              </Typography>
              {group.id === 'operation' && (
                <Stack direction="row" spacing={0.5} sx={{ mr: 0.75 }}>
                  <NotificationCount count={counts.abandonedCarts} label={cartLabel} tone="warning" />
                  <NotificationCount count={counts.pendingAccessRequests} label={requestLabel} />
                </Stack>
              )}
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  color: 'text.secondary',
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 200ms ease',
                }}
              />
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <List dense disablePadding sx={{ pb: 1 }}>
                {group.links.map((link) => {
                  const selected = link.to === activeLink;
                  return (
                    <ListItemButton
                      key={link.to}
                      component={RouterLink}
                      to={link.to}
                      selected={selected}
                      sx={{
                        minHeight: 40,
                        ml: 2.75,
                        mr: 1,
                        my: 0.25,
                        pl: 1.5,
                        borderRadius: 1.5,
                        borderLeft: '2px solid',
                        borderLeftColor: selected ? 'primary.main' : 'divider',
                        color: selected ? 'primary.main' : 'text.secondary',
                        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: SELECTED_BG },
                        '&:hover': { bgcolor: HOVER_BG, color: 'text.primary' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={selected ? 700 : 500} sx={{ flexGrow: 1, minWidth: 0 }}>
                        {link.title}
                      </Typography>
                      {link.to === ROUTES.ADMIN_ABANDONED_CARTS && (
                        <NotificationCount count={counts.abandonedCarts} label={cartLabel} tone="warning" />
                      )}
                      {link.to === ROUTES.ADMIN_ACCESS_REQUESTS && (
                        <NotificationCount count={counts.pendingAccessRequests} label={requestLabel} />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
};

const AdminLayout: React.FC = () => {
  const location = useLocation();
  const { displayName, logout } = useStoreAdminContext();
  const { tenantCode, tenant } = useTenant();
  const { theme: storeTheme, features } = useStorefrontContext();
  const storePaused = Boolean(features.storePaused);
  const isLayoutEditor = location.pathname === ROUTES.ADMIN_LAYOUT;

  const tenantLabel = [tenantCode, storeTheme?.siteName || tenant.siteName]
    .filter(Boolean)
    .join(' · ');

  const sidebar = <AdminSidebar />;

  if (isLayoutEditor) {
    return (
      <>
        <PageMeta title="Layout — Página de inicio" />
        <ErrorBoundary variant="page">
          <Outlet />
        </ErrorBoundary>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Administración de tienda" />
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
            Admin tienda
          </Typography>
          {storePaused && (
            <Chip
              size="small"
              color="warning"
              label="Tienda pausada"
              component={RouterLink}
              to={ROUTES.ADMIN_FEATURES}
              clickable
            />
          )}
          {tenantLabel && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={tenantLabel}
              sx={{ mr: 'auto' }}
            />
          )}
          {!tenantLabel && <Box sx={{ flexGrow: 1 }} />}
          {displayName && (
            <Typography variant="body2" color="text.secondary">
              {displayName}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            v{buildInfo.version} · Build {buildInfo.buildNumber}
          </Typography>
          <Button component={RouterLink} to={ROUTES.HOME} color="inherit">
            Ver tienda
          </Button>
          <Button color="inherit" onClick={logout}>
            Salir
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
          {sidebar}
          <Box flexGrow={1}>
            <ErrorBoundary variant="page">
              <Outlet />
            </ErrorBoundary>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default AdminLayout;
